'use client';

import { useState, useCallback, useMemo } from 'react';
import {
  useProjectReferences,
  useDeleteProjectReference,
  useUpdateProjectReferenceCategory,
  useUpdateProjectReference,
} from './use-project-references';
import type {
  ProjectReference,
  ProjectReferenceCategory,
} from '@/types/project-reference';
import {
  isImageReference,
  isDocumentReference,
  isSystemReference,
} from '@/types/project-reference';

interface UseProjectReferencesPanelOptions {
  enablePolling?: boolean;
}

export interface EnlargedImage {
  src: string;
  alt?: string;
}

export function useProjectReferencesPanel(
  projectId: string,
  options: UseProjectReferencesPanelOptions = {}
) {
  const { enablePolling = true } = options;

  const [enlargedImage, setEnlargedImage] = useState<EnlargedImage | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedReference, setSelectedReference] =
    useState<ProjectReference | null>(null);

  // Fetch references with automatic polling for pending items
  const {
    data: references = [],
    isLoading,
    refetch,
  } = useProjectReferences(projectId, { enablePolling });

  // Mutations
  const deleteMutation = useDeleteProjectReference(projectId);
  const updateCategoryMutation = useUpdateProjectReferenceCategory(projectId);
  const updateReferenceMutation = useUpdateProjectReference(projectId);

  const handleDelete = useCallback(
    async (referenceId: string) => {
      try {
        setDeletingId(referenceId);
        await deleteMutation.mutateAsync(referenceId);
        // Close detail dialog if deleting the currently selected reference
        if (selectedReference?.id === referenceId) {
          setSelectedReference(null);
        }
      } catch (error) {
        console.error('Failed to delete reference:', error);
      } finally {
        setDeletingId(null);
      }
    },
    [deleteMutation, selectedReference?.id]
  );

  const handleCategoryChange = useCallback(
    async (referenceId: string, category: ProjectReferenceCategory) => {
      try {
        await updateCategoryMutation.mutateAsync({ referenceId, category });
      } catch (error) {
        console.error('Failed to update category:', error);
      }
    },
    [updateCategoryMutation]
  );

  const handleUploadSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleEnlarge = useCallback((src: string, alt?: string) => {
    setEnlargedImage({ src, alt });
  }, []);

  const handleCloseEnlarged = useCallback(() => {
    setEnlargedImage(null);
  }, []);

  // Detail dialog handlers
  const handleOpenDetail = useCallback((reference: ProjectReference) => {
    setSelectedReference(reference);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedReference(null);
  }, []);

  const handleSaveDetail = useCallback(
    async (data: {
      category?: ProjectReferenceCategory;
      description?: string | null;
    }) => {
      if (!selectedReference) return;
      try {
        await updateReferenceMutation.mutateAsync({
          referenceId: selectedReference.id,
          ...data,
        });
        setSelectedReference(null);
      } catch (error) {
        console.error('Failed to update reference:', error);
      }
    },
    [selectedReference, updateReferenceMutation]
  );

  // Separate user and system references, then by type
  const categorizedReferences = useMemo(() => {
    const userReferences = references.filter((ref) => !isSystemReference(ref));
    const systemReferences = references.filter(isSystemReference);

    return {
      userReferences,
      systemReferences,
      userImageReferences: userReferences.filter(isImageReference),
      userDocumentReferences: userReferences.filter(isDocumentReference),
      systemImageReferences: systemReferences.filter(isImageReference),
    };
  }, [references]);

  return {
    // State
    isLoading,
    deletingId,
    enlargedImage,
    selectedReference,

    // References
    ...categorizedReferences,

    // Actions
    handleDelete,
    handleCategoryChange,
    handleUploadSuccess,
    handleEnlarge,
    handleCloseEnlarged,
    handleOpenDetail,
    handleCloseDetail,
    handleSaveDetail,
    isSavingDetail: updateReferenceMutation.isPending,
    isDeletingDetail: deleteMutation.isPending,
  };
}
